import { action } from '@ember/object';
import Component from '@glimmer/component';
import { sub, add, startOfDay, endOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  DEFAULT_TIMEZONE,
  endOfDayFromQP,
  modifyDateInTimeZone,
  startOfDayFromQP,
} from 'garaje/utils/date-fns-tz-utilities';

export const API_DATE_FORMAT = 'yyyy-MM-dd';

// Single source of truth for the preset filter vocabulary. These exact strings
// are persisted by the parent controller as the `selectedDateRange` query
// param, which is why the picker keeps its own preset list rather than mapping
// onto <DateRanger>'s @shortcuts vocabulary.
const DATE_RANGE_FILTER_OPTIONS = [
  'Today',
  'Yesterday',
  'Last 7 days',
  'Last 30 days',
  'Last 90 days',
  'Past year',
  'Tomorrow',
  'Next 7 days',
  'Next 30 days',
  'Next 90 days',
  'All Time',
] as const;

const ALL_TIME_FILTER = 'All Time';
const TODAY_FILTER = 'Today';
const DEFAULT_FILTER = ALL_TIME_FILTER;
const CUSTOM_FILTER = 'Custom';

type DateValue = string | Date | null | undefined;

export interface DateRangeSelectComponentArgs {
  /**
   * Called when the selected range changes. `start`/`end` are yyyy-MM-dd
   * strings, or null for "All Time".
   */
  onDateRangeChange: (dateRangeFilter: string, start: string | null, end: string | null) => unknown;
  /**
   * the selected filter option
   */
  dateRangeFilter: string;
  startDate?: DateValue;
  endDate?: DateValue;
  // IANA timezone of the property being viewed; all ranges resolve here.
  timezone?: string;
}

// Built on the shared <DateRanger>; ranges resolve in the property timezone so
// the calendar and feed track the property's day, not the browser/moment tz.
export default class DateRangeSelectComponent extends Component<DateRangeSelectComponentArgs> {
  dateRangeFilterOptions = DATE_RANGE_FILTER_OPTIONS;

  get timezone(): string {
    return this.args.timezone ?? DEFAULT_TIMEZONE;
  }

  get isDefaultFilter(): boolean {
    return this.dateRangeFilter === DEFAULT_FILTER;
  }

  get isToday(): boolean {
    return this.dateRangeFilter === TODAY_FILTER;
  }

  get dateRangeFilter(): string {
    return this.args.dateRangeFilter ?? DEFAULT_FILTER;
  }

  // Effective range fed to <DateRanger>, as property-tz day boundaries. Custom
  // ranges come from the stored args; presets are derived from the preset.
  get startDate(): Date {
    if (this.dateRangeFilter === CUSTOM_FILTER) return this.#parseArgDate(this.args.startDate, 'start');
    return this.#getRangeForFilter(this.dateRangeFilter)[0];
  }

  get endDate(): Date {
    if (this.dateRangeFilter === CUSTOM_FILTER) return this.#parseArgDate(this.args.endDate, 'end');
    return this.#getRangeForFilter(this.dateRangeFilter)[1];
  }

  @action
  selectDateRange(option: string): void {
    if (option === ALL_TIME_FILTER) {
      this.args.onDateRangeChange(option, null, null);
      return;
    }

    this.args.onDateRangeChange(option, ...this.#getRangeForFilter(option, true));
  }

  // <DateRanger> emits Date instants already resolved in @timezone; serialize
  // them back to yyyy-MM-dd in that same tz so the day round-trips exactly.
  @action
  didSelectDateRange(startDate: Date, endDate: Date): void {
    this.args.onDateRangeChange(
      CUSTOM_FILTER,
      formatInTimeZone(startDate, this.timezone, API_DATE_FORMAT),
      formatInTimeZone(endDate, this.timezone, API_DATE_FORMAT),
    );
  }

  #parseArgDate(value: DateValue, boundary: 'start' | 'end'): Date {
    const tz = this.timezone;
    if (value instanceof Date) return modifyDateInTimeZone(value, tz, boundary === 'start' ? startOfDay : endOfDay);

    // Fail closed on a malformed/empty QP string — these parsers regex-validate and
    // fall back to today rather than returning an Invalid Date that crashes the picker.
    return boundary === 'start' ? startOfDayFromQP(value, tz) : endOfDayFromQP(value, tz);
  }

  #getRangeForFilter(filter: string): [Date, Date];
  #getRangeForFilter(filter: string, formatForApi?: boolean): [string, string];
  #getRangeForFilter(filter: string, formatForApi?: boolean): [string | Date, string | Date] {
    let start = new Date();
    let end = new Date();

    switch (filter) {
      case 'Yesterday':
        start = sub(start, { days: 1 });
        end = sub(end, { days: 1 });
        break;
      case 'Last 7 days':
        start = sub(start, { days: 7 });
        break;
      case 'Last 30 days':
        start = sub(start, { days: 30 });
        break;
      case 'Last 90 days':
        start = sub(start, { days: 90 });
        break;
      case 'Past year':
        start = sub(start, { years: 1 });
        break;
      case 'Tomorrow':
        start = add(start, { days: 1 });
        end = add(end, { days: 1 });
        break;
      case 'Next 7 days':
        end = add(end, { days: 7 });
        break;
      case 'Next 30 days':
        end = add(end, { days: 30 });
        break;
      case 'Next 90 days':
        end = add(end, { days: 90 });
        break;
    }

    const tz = this.timezone;
    const startOfRange = modifyDateInTimeZone(start, tz, startOfDay);
    const endOfRange = modifyDateInTimeZone(end, tz, endOfDay);

    if (formatForApi === true) {
      return [formatInTimeZone(startOfRange, tz, API_DATE_FORMAT), formatInTimeZone(endOfRange, tz, API_DATE_FORMAT)];
    }

    return [startOfRange, endOfRange];
  }
}
